import { ComponentProps, useMemo, useState } from 'react';

import {
  LockIcon,
  LockOpenIcon,
  Maximize2Icon,
  Minimize2Icon,
  PlusIcon,
  RotateCwIcon,
  Trash2Icon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Button } from '@/components/ui/button';
import { Autocomplete, AutocompleteOption } from '@/components/ui/custom/Autocomplete/Autocomplete';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { userContextVariables } from '@/modules/tryIt/components/VariablesSection/constants';

import { JsonVariablesEditor } from './JsonVariablesEditor';
import { useContextVariables, useVariableLabel } from '../../../../hooks/useContextVariables';
import { useIsTextTruncated } from '../../../../hooks/useIsTextTruncated';
import {
  LockableVariable,
  useLockedContextVariables,
} from '../../../../hooks/useLockedContextVariables';
import { fromContextJson, toContextJson } from '../../../../utils/contextVariablesJson';

const byLabel = (a: LockableVariable, b: LockableVariable) => a.label.localeCompare(b.label);

export type CommitResult = 'applied' | 'locked' | 'invalid';

const rowFieldsClass = (expanded: boolean) =>
  cn('flex min-w-0 flex-1 gap-1.5', expanded ? 'flex-col items-start' : 'items-center');

const PLACEHOLDER_DRAFT_ID = 'placeholder-draft';

type TruncatingInputProps = Omit<ComponentProps<typeof Input>, 'value' | 'ref'> & { value: string };

/** Input that shows its full text in a hover tooltip when it overflows. */
const TruncatingInput = ({ value, className, ...props }: TruncatingInputProps) => {
  const truncation = useIsTextTruncated(value);
  return (
    <InfoTooltip content={truncation.isTruncated ? value : undefined} side="top" size="large">
      <Input
        ref={truncation.ref}
        value={value}
        className={cn('text-ellipsis', className)}
        {...props}
      />
    </InfoTooltip>
  );
};

// `onMouseDown` preventDefault keeps a button click from blurring (and committing) the value input.
const LockToggleButton = ({
  locked,
  onToggle,
  label,
  disabled,
}: {
  locked: boolean;
  onToggle: () => void;
  label?: string;
  disabled?: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <Button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      variant="ghost-neutral"
      size="icon-md"
      onClick={onToggle}
      disabled={disabled}
      aria-label={label ?? t('try_it.modal.toggle_lock')}
      aria-pressed={locked}
      className={cn('shrink-0', locked && 'border border-primary [&_svg]:text-primary')}
    >
      {locked ? <LockIcon size={16} /> : <LockOpenIcon size={16} />}
    </Button>
  );
};

const DeleteButton = ({ onDelete, disabled }: { onDelete: () => void; disabled?: boolean }) => {
  const { t } = useTranslation();
  return (
    <Button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      variant="ghost-neutral"
      size="icon-md"
      onClick={onDelete}
      disabled={disabled}
      aria-label={t('common.delete')}
      className="shrink-0 text-muted-foreground hover:text-destructive hover:[&_svg]:text-destructive"
    >
      <Trash2Icon size={16} />
    </Button>
  );
};

type ExpandableValueFieldProps = {
  value: string;
  onChange: (value: string) => void;
  onCommit: () => void;
  disabled?: boolean;
  expanded: boolean;
  onToggleExpanded: (expanded: boolean) => void;
};

// `expanded` is owned by the row since it also drives the row's stacked layout.
const ExpandableValueField = ({
  value,
  onChange,
  onCommit,
  disabled,
  expanded,
  onToggleExpanded,
}: ExpandableValueFieldProps) => {
  const { t } = useTranslation();
  return (
    <div className={cn('relative', expanded ? 'w-full' : 'min-w-0 flex-1')}>
      {expanded ? (
        <>
          <Textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onBlur={onCommit}
            disabled={disabled}
            placeholder={t('try_it.context.value_placeholder')}
            rows={4}
            className="resize-y pr-9"
          />
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onToggleExpanded(false)}
            aria-label={t('common.collapse')}
            className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"
          >
            <Minimize2Icon size={14} />
          </button>
        </>
      ) : (
        <>
          <TruncatingInput
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onBlur={onCommit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onCommit();
              }
            }}
            disabled={disabled}
            placeholder={t('try_it.context.value_placeholder')}
            size="sm"
            className="pr-9"
          />
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onToggleExpanded(true)}
            aria-label={t('common.expand')}
            className="absolute top-1/2 right-2 -translate-y-1/2 text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-foreground"
          >
            <Maximize2Icon size={14} />
          </button>
        </>
      )}
    </div>
  );
};

type RowProps = {
  variable: LockableVariable;
  displayLabel: string;
  onCommitValue: (label: string, value: string) => void;
  onRemove: (label: string) => void;
  onToggleLock: (variable: LockableVariable) => void;
};

const ContextVariableRow = ({
  variable,
  displayLabel,
  onCommitValue,
  onRemove,
  onToggleLock,
}: RowProps) => {
  const [value, setValue] = useState(variable.value);
  const [committed, setCommitted] = useState(variable.value);
  const [expanded, setExpanded] = useState(false);

  if (variable.value !== committed) {
    setCommitted(variable.value);
    setValue(variable.value);
  }

  const commit = () => {
    if (value !== variable.value) {
      onCommitValue(variable.label, value);
    }
  };

  return (
    <div className={cn('group flex w-full gap-2', expanded ? 'items-start' : 'items-center')}>
      <LockToggleButton locked={variable.isLocked} onToggle={() => onToggleLock(variable)} />
      <div className={rowFieldsClass(expanded)}>
        <TruncatingInput
          value={displayLabel}
          readOnly
          size="sm"
          className={cn('text-muted-foreground', expanded ? 'w-full' : 'w-42 shrink-0')}
        />
        <ExpandableValueField
          value={value}
          onChange={setValue}
          onCommit={commit}
          disabled={variable.isLocked}
          expanded={expanded}
          onToggleExpanded={setExpanded}
        />
      </div>
      <DeleteButton onDelete={() => onRemove(variable.label)} disabled={variable.isLocked} />
    </div>
  );
};

type DraftRowProps = {
  options: AutocompleteOption[];
  canRemove: boolean;
  onCommit: (label: string, value: string, locked: boolean) => void;
  onRemove: () => void;
};

const DraftVariableRow = ({ options, canRemove, onCommit, onRemove }: DraftRowProps) => {
  const { t } = useTranslation();
  const [label, setLabel] = useState('');
  const [value, setValue] = useState('');
  const [locked, setLocked] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const commit = () => {
    if (label && value) {
      onCommit(label, value, locked);
    }
  };

  return (
    <div className={cn('group flex w-full gap-2', expanded ? 'items-start' : 'items-center')}>
      <LockToggleButton locked={locked} onToggle={() => setLocked((prev) => !prev)} />
      <div className={rowFieldsClass(expanded)}>
        <Autocomplete
          // w-42 mirrors the committed row's name column so both rows line up.
          className={cn(
            '[&_[data-truncation-target]]:min-w-0 [&_button]:h-9',
            expanded ? 'w-full' : 'w-42 shrink-0'
          )}
          options={options}
          value={label}
          onChange={setLabel}
          onCreateNew={setLabel}
          placeholder={t('try_it.context.variable_placeholder')}
          searchPlaceholder={t('common.search_dots')}
        />
        <ExpandableValueField
          value={value}
          onChange={setValue}
          onCommit={commit}
          expanded={expanded}
          onToggleExpanded={setExpanded}
        />
      </div>
      {canRemove ? <DeleteButton onDelete={onRemove} /> : <span className="size-9 shrink-0" />}
    </div>
  );
};

/** "Context variables" editor for test-mode Try It: committed rows + editable draft rows, always ≥1. */
export const ContextVariablesSection = () => {
  const { t } = useTranslation();
  const variableLabel = useVariableLabel();
  const { context, setVariable, removeVariable, removeVariables } = useContextVariables();
  const { lockableVariables, toggleLock, setAllLocked, replaceUnlocked } =
    useLockedContextVariables();
  const [showJson, setShowJson] = useState(false);

  const [draftIds, setDraftIds] = useState<string[]>([]);

  const sortedVariables = useMemo(() => [...lockableVariables].sort(byLabel), [lockableVariables]);
  const draftRowIds =
    sortedVariables.length === 0 && draftIds.length === 0 ? [PLACEHOLDER_DRAFT_ID] : draftIds;
  const json = useMemo(() => (showJson ? toContextJson(context) : ''), [showJson, context]);

  // Offer the predefined user variables (translated) that aren't already set;
  // custom names come through `onCreateNew`.
  const variableOptions = useMemo<AutocompleteOption[]>(
    () =>
      userContextVariables
        .filter((option) => !lockableVariables.some((v) => v.label === option.label))
        .map((option) => ({
          value: option.label,
          label: variableLabel(option.label),
          category: t('analytics.fields.user'),
        })),
    [lockableVariables, variableLabel, t]
  );

  const addDraft = () => {
    const id = uuidv4();
    setDraftIds((prev) => [...prev, id]);
  };

  const removeDraft = (id: string) =>
    setDraftIds((prev) => prev.filter((draftId) => draftId !== id));

  const removeVariableRow = (label: string) => removeVariable(label);

  const showClearAll = showJson
    ? lockableVariables.length > 0
    : lockableVariables.length + draftRowIds.length > 1;

  // Parity with the legacy reset: locked variables survive a "Clear all".
  const clearAllUnlocked = () => {
    const unlocked = lockableVariables.filter((variable) => !variable.isLocked);
    removeVariables(unlocked.map((variable) => variable.label));
    setDraftIds([]);
  };

  // The length guard matters: `every` on an empty list is true.
  const jsonLocked = sortedVariables.length > 0 && sortedVariables.every((v) => v.isLocked);
  const toggleLockAll = () => setAllLocked(!jsonLocked);

  const commitJson = (text: string): CommitResult => {
    const parsed = fromContextJson(text);
    if (!parsed) {
      return 'invalid';
    }
    return replaceUnlocked(parsed).skippedLocked ? 'locked' : 'applied';
  };

  const commitDraft = (id: string, label: string, value: string, locked: boolean) => {
    setVariable(label, value);
    if (locked) {
      // The hook only exposes a toggle; `isLocked: false` flips it on.
      toggleLock({ label, value, isLocked: false });
    }
    setDraftIds((prev) => prev.filter((draftId) => draftId !== id));
  };

  return (
    <div className="flex flex-col items-start gap-4 self-stretch">
      <div className="flex w-full items-start justify-between gap-2 pb-3">
        <div className="flex flex-1 flex-col items-start gap-1">
          <span className="text-sm leading-5 font-medium text-foreground">
            {t('try_it.context.variables_label')}
          </span>
          <p className="max-w-sm self-stretch text-sm leading-5 font-normal text-muted-foreground">
            {t('try_it.context.variables_helper')}
          </p>
        </div>
        {showClearAll && (
          <Button type="button" variant="ghost-neutral" size="flush" onClick={clearAllUnlocked}>
            <RotateCwIcon />
            {t('common.clear_all')}
          </Button>
        )}
      </div>

      <div className="flex flex-col items-start gap-2 self-stretch pl-11">
        <div className="flex items-center gap-2">
          <Label
            htmlFor="try-it-context-json"
            className="text-sm leading-none font-medium text-foreground"
          >
            {t('try_it.context.show_as_json')}
          </Label>
          <Switch id="try-it-context-json" checked={showJson} onCheckedChange={setShowJson} />
        </div>
      </div>

      {showJson ? (
        <div className="flex items-start gap-2 self-stretch">
          <LockToggleButton
            locked={jsonLocked}
            onToggle={toggleLockAll}
            label={t('try_it.context.toggle_lock_all')}
            disabled={sortedVariables.length === 0}
          />
          <JsonVariablesEditor json={json} onCommit={commitJson} readOnly={jsonLocked} />
        </div>
      ) : (
        <>
          <div className="flex w-full flex-col gap-2">
            {sortedVariables.map((variable) => (
              <ContextVariableRow
                key={variable.label}
                variable={variable}
                displayLabel={variableLabel(variable.label)}
                onCommitValue={setVariable}
                onRemove={removeVariableRow}
                onToggleLock={toggleLock}
              />
            ))}

            {draftRowIds.map((id) => (
              <DraftVariableRow
                key={id}
                options={variableOptions}
                canRemove={sortedVariables.length > 0 || draftRowIds.length > 1}
                onCommit={(label, value, locked) => commitDraft(id, label, value, locked)}
                onRemove={() => removeDraft(id)}
              />
            ))}
          </div>

          {/* Styled like the AI Agent settings (Reminder message) button; the `size-9` spacer aligns it with the dropdown column. */}
          <div className="flex w-full items-center gap-2">
            <span className="size-9 shrink-0" />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-auto w-fit gap-1 p-0 text-primary hover:bg-transparent hover:text-primary/90"
              onClick={addDraft}
            >
              <PlusIcon className="h-4 w-4 text-muted-foreground" />
              {t('try_it.context.add_another')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
